import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Button as MuiButton,
  Paper,
  Container,
  Radio,
  CircularProgress,
  useTheme,
} from "@mui/material";
import waterTap from "@/assets/images/icons/waterTap.svg";
import infoIcon from "@/assets/images/icons/infoIcon.svg";
import Toilet from "@/assets/images/icons/dashboardToiletImage.svg";
import HandWash from "@/assets/images/icons/dashboardHandwashImage.svg";
import OperationAndMaintenance from "@/assets/images/icons/dashboardOperationAndMaintanceImage.svg";
import BehaviourChanege from "@/assets/images/icons/dashboardBehaviorChangeImage.svg";
import EcoFriendly from "@/assets/images/icons/dashboardEcoFriendlyImage.svg";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../api/Axios";
import Button from "../../../shared/button/Button";
import { getRoleData } from "../../../shared/functions/commonfunction";
import DataLoader from "../../../shared/DataLoader/DataLoader";
import { useTranslation } from "react-i18next";

const iconMap = {
  Water: waterTap,
  Toilet: Toilet,
  "Handwashing with Soap": HandWash,
  "Operation and Maintenance": OperationAndMaintenance,
  "Behavior Change and Capacity Building": BehaviourChanege,
  "Mission LiFE activities": EcoFriendly,
};

const CategoryPreview = () => {
  const { categoryId } = useParams();
  const [questionData, setQuestionData] = useState<any>(null);
  const [answerData, setAnswerData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [categoryIcons, setCategoryIcons] = useState<{ [key: number]: string }>(
    {}
  );
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { t } = useTranslation("dashboard");

  // Function to fetch category icons
  const fetchCategoryIcons = async () => {
    try {
      const response = await API.get("/master/survey-category-master");
      const iconsMap: { [key: number]: string } = {};

      response?.data?.data?.forEach((category: any) => {
        if (category.category_id && category.category_icon) {
          iconsMap[category.category_id] = `${
            import.meta.env.VITE_API_BASE_URL
          }${category.category_icon}`;
        }
      });

      setCategoryIcons(iconsMap);
    } catch (err) {
      console.log("error fetching master data", err);
    }
  };

  // Fetch category icons on component mount
  useEffect(() => {
    fetchCategoryIcons();
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [qRes, aRes] = await Promise.all([
          API.get("/survey/get-questions"),
          API.get("/survey/get-questions-answer"),
        ]);
        setQuestionData(qRes?.data?.data);
        setAnswerData(aRes?.data?.data?.survey_submission_questions);
        if (aRes?.data?.data?.survey_submission_info?.status !== "submitted") {
          navigate("/school-dashboard");
        }
      } catch (err) {
        // Optionally handle error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [categoryId]);

  // Helper: getFilteredQuestions (copied from PreviewAndSubmit)
  const getFilteredQuestions = (
    categoryQuestions: any[],
    notForSchoolTypes: any,
    schoolCategory: any
  ) => {
    let filteredQuestions = [...categoryQuestions];
    if (
      notForSchoolTypes &&
      schoolCategory &&
      notForSchoolTypes[schoolCategory]
    ) {
      const nf = notForSchoolTypes[schoolCategory];
      // Remove questions by questionIds
      // if (nf.questionIds && nf.questionIds.length > 0) {
      //   filteredQuestions = filteredQuestions.filter(
      //     (q) => !nf.questionIds.includes(q.questionId)
      //   );
      // }
      // Remove options by questionOptions
      if (nf.questionOptions && nf.questionOptions?.length > 0) {
        filteredQuestions = filteredQuestions.map((q) => {
          const removeOptions = nf.questionOptions.filter(
            (qo) => qo.questionId === q.questionId
          );
          if (removeOptions?.length > 0 && q.options) {
            const removeOptionIds = removeOptions.map((qo) => qo.optionId);
            return {
              ...q,
              options: q.options.filter(
                (opt: any) => !removeOptionIds.includes(opt.optionId)
              ),
            };
          }
          return q;
        });
      }
    }
    return filteredQuestions;
  };

  // Helper: getQuestionNumber (copied from PreviewAndSubmit)
  const getQuestionNumber = (
    categories: any[],
    notForSchoolTypes: any,
    schoolCategory: any,
    categoryId: number,
    questionIndex: number
  ) => {
    let totalQuestions = 0;
    for (let i = 0; i < categories?.length; i++) {
      const category = categories[i];
      if (category.categoryId === categoryId) {
        break;
      }
      const filteredQuestions = getFilteredQuestions(
        category.questions || [],
        notForSchoolTypes,
        schoolCategory
      );
      totalQuestions += filteredQuestions.length;
    }
    return totalQuestions + questionIndex + 1;
  };

  // Add getQuestionDisplayNumbers utility
  function getQuestionDisplayNumbers(allQuestions: any[]) {
    const displayNumbers: Record<number, string> = {};
    let mainNumber = 1;
    for (let i = 0; i < allQuestions?.length; i++) {
      const q = allQuestions[i];
      if (q.static_question_number && q.display_question_number) {
        displayNumbers[q.questionId] = q.display_question_number;
      } else {
        displayNumbers[q.questionId] = `${mainNumber}`;
        mainNumber++;
      }
    }
    return displayNumbers;
  }

  // Get current category
  let category: any = null;
  let questions: any[] = [];
  let categoryIcon = waterTap;
  let categoryName = "";
  let subtitle = "Key indicative norms and standards";
  const notForSchoolTypes = questionData?.notForSchoolTypes;
  const schoolCategory = getRoleData()?.school_ques_cat;
  if (questionData && categoryId) {
    category = questionData.categories?.find(
      (cat: any) => String(cat.categoryId) === String(categoryId)
    );
    if (category && typeof category === "object") {
      questions = getFilteredQuestions(
        category.questions || [],
        notForSchoolTypes,
        schoolCategory
      );
      categoryName = category.categoryName;
      subtitle = category.subtitle || subtitle;
      categoryIcon =
        categoryIcons[category.categoryId] ||
        iconMap[category.categoryName] ||
        waterTap;
    }
  }

  // Prepare filtered questions (only those with answers)
  let filteredQuestions: any[] = [];
  if (questions && answerData) {
    filteredQuestions = questions.filter((q: any) => {
      // For multi_input, answer is an object; for others, string/number
      const ans = answerData[q.questionId];
      if (q.questionType === "multi_input") {
        return (
          ans &&
          typeof ans === "object" &&
          Object.values(ans).some((v) => v !== null && v !== "")
        );
      }
      return ans !== undefined && ans !== null && ans !== "";
    });
  }

  // Build a flat list of all questions in all categories, in order
  const allQuestionsFlat = questionData?.categories?.reduce(
    (acc: any[], category: any) => {
      const filteredQuestions = getFilteredQuestions(
        category.questions || [],
        notForSchoolTypes,
        schoolCategory
      );
      return [...acc, ...filteredQuestions];
    },
    []
  );
  const questionDisplayNumbers = getQuestionDisplayNumbers(allQuestionsFlat);

  // Helper to get answer label (like PreviewAndSubmit)
  const getAnswerLabel = (question: any, answer: any) => {
    if (!question || answer === undefined || answer === null) return "";
    if (question.questionType === "single-select") {
      const opt = question.options?.find(
        (o: any) => String(o.optionId) === String(answer)
      );
      return opt?.label || "";
    }
    if (question.questionType === "input") {
      return answer == 0 || answer ? answer : "";
    }
    if (question.questionType === "multi_input") {
      if (!answer || typeof answer !== "object") return "";
      return Object.entries(answer)
        .filter(([_, v]) => v !== null && v !== "")
        .map(([k, v]) => {
          const opt = question.options?.find(
            (o: any) => String(o.optionId) === String(k)
          );
          return opt ? `${opt.label}: ${v}` : v;
        })
        .join(", ");
    }
    return "";
  };

  // Get the original question index in the category
  const getOriginalQuestionIndex = (questionId: number) => {
    return questions.findIndex((q: any) => q.questionId === questionId);
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            minHeight: "80vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDarkMode ? "#1A1D21" : "#EFF9FF",
          }}
        >
          <DataLoader />
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "calc(100vh - 150px)",
            width: "100%",
            background: isDarkMode
              ? "#1A1D21"
              : "linear-gradient(180deg, #DFFCF5 0%, #EEFFF0 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: { xs: 2.5, md: 4 },
          }}
        >
          <Container
            maxWidth={false}
            sx={{ width: { xs: "100%", md: "88%" }, px: { xs: 2, md: 0 } }}
          >
            <Box
              sx={{
                minHeight: { xs: "calc(100vh - 175px)", md: "calc(100vh - 190px)" },
                border: `1px solid ${isDarkMode ? "#596268" : "#49DEC8"}`,
                borderRadius: { xs: "22px 22px 0 0", md: "34px 34px 0 0" },
                borderBottom: "none",
                background: isDarkMode
                  ? "linear-gradient(180deg, #22282D 0%, #29322F 100%)"
                  : "linear-gradient(180deg, rgba(248,252,255,.96) 0%, rgba(235,250,237,.96) 100%)",
                display: "flex",
                flexDirection: "column",
                px: { xs: 1.5, sm: 3, md: 5 },
                pt: { xs: 2.5, md: 4 },
              }}
            >
              <Box
                sx={{
                  backgroundColor: isDarkMode ? "#242B30" : "#FFFFFF",
                  border: `1px solid ${isDarkMode ? "#4E585E" : "#D4D4D4"}`,
                  borderRadius: { xs: "18px", md: "32px" },
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 2, md: 2.5 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    pb: 2,
                    mb: 2,
                    borderBottom: `1px solid ${
                      isDarkMode ? "#495158" : "#D4D4D4"
                    }`,
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      background: isDarkMode ? "#343A40" : "#E9E7FF",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={categoryIcon}
                      alt={`${categoryName} Icon`}
                      sx={{
                        width: 40,
                        height: 40,
                        filter: isDarkMode
                          ? "invert(1)"
                          : "none",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: isDarkMode ? "#fff" : "#0F202B",
                        fontSize: {
                          xs: `calc(1.15rem * var(--dynamic-font-size, 1))`,
                          md: `calc(1.35rem * var(--dynamic-font-size, 1))`,
                        },
                      }}
                    >
                      {categoryName}
                    </Typography>
                    <Typography
                      sx={{
                        color: isDarkMode ? "#B8C0C5" : "#606060",
                        fontSize: {
                          xs: `calc(.86rem * var(--dynamic-font-size, 1))`,
                          md: `calc(.98rem * var(--dynamic-font-size, 1))`,
                        },
                      }}
                    >
                      {filteredQuestions.length} Questions Answered
                    </Typography>
                  </Box>
                </Box>
              {!questions ||
                questions.length === 0 ||
                filteredQuestions.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 150,
                    p: 3,
                  }}
                >
                  <Typography
                    sx={{
                      color: isDarkMode ? "#bfc2d9" : "#5C5C5C",
                      fontSize: {
                        xs: `calc(1rem * var(--dynamic-font-size, 1))`,
                        sm: `calc(1.05rem * var(--dynamic-font-size, 1))`,
                        md: `calc(1.13rem * var(--dynamic-font-size, 1))`,
                      },
                      textAlign: "center",
                      fontWeight: 500,
                    }}
                  >
                    {t("noQuestionEligible")}
                  </Typography>
                </Box>
              ) : (
                filteredQuestions.map((question: any) => {
                  const questionNumber =
                    questionDisplayNumbers[question.questionId];
                  const answer = answerData[question.questionId];
                  const answerLabel = getAnswerLabel(question, answer);

                  return (
                    <Box
                      key={question.questionId}
                      sx={{
                        py: { xs: 1.5, md: 2 },
                        borderBottom: `1px solid ${
                          isDarkMode ? "#495158" : "#D7D7D7"
                        }`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? "#fff" : "#0F202B",
                          fontSize: {
                            xs: `calc(1rem * var(--dynamic-font-size, 1))`,
                            sm: `calc(1.05rem * var(--dynamic-font-size, 1))`,
                            md: `calc(1.13rem * var(--dynamic-font-size, 1))`,
                          },
                          mb: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ color: isDarkMode ? "#A7A7A7" : "#1A5F20" }}
                        >
                          {`Q${questionNumber}. `}
                        </Box>
                        {question.questionText}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1.25,
                          px: { xs: 1.5, md: 2 },
                          py: 1.25,
                          borderRadius: "8px",
                          backgroundColor: isDarkMode ? "#39423F" : "#E1F7E7",
                        }}
                      >
                        <Typography
                          sx={{
                            color: isDarkMode ? "#D2D5D3" : "#1A5F20",
                            fontSize: {
                              xs: `calc(0.9rem * var(--dynamic-font-size, 1))`,
                              sm: `calc(0.95rem * var(--dynamic-font-size, 1))`,
                              md: `calc(0.98rem * var(--dynamic-font-size, 1))`,
                            },
                          }}
                        >
                          {answerLabel}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: { xs: 3, md: 5 },
                mb: { xs: 3, md: 4 },
                width: "100%",
              }}
            >
              <Button
                text={t(`previewAndSubmit.back`)}
                variant="contained"
                sx={{
                  maxWidth: "170px",
                  px: 5,
                  height: 48,
                  borderRadius: "999px !important",
                }}
                onClick={() => navigate(-1)}
              />
            </Box>
            <Box
              component="footer"
              sx={{
                mx: { xs: -1.5, sm: -3, md: -5 },
                mt: "auto",
                py: 2.5,
                borderTop: `1px solid ${
                  isDarkMode ? "#465047" : "#D3DDD5"
                }`,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  color: isDarkMode ? "#AEB4B0" : "#737A76",
                  fontSize: {
                    xs: `calc(.75rem * var(--dynamic-font-size, 1))`,
                    md: `calc(.9rem * var(--dynamic-font-size, 1))`,
                  },
                }}
              >
                © {new Date().getFullYear()} Ministry of Education, Govt. of India. All rights
                reserved.
              </Typography>
            </Box>
            </Box>
          </Container>
        </Box>
      )}{" "}
    </>
  );
};

export default CategoryPreview;
